import { useState } from 'react';
import { HsmActiveTab } from '../types';
import { useHsmContacts, UseHsmContactsReturn } from './useHsmContacts';
import { useHsmBatches, UseHsmBatchesReturn } from './useHsmBatches';
import { useHsmCampaigns, UseHsmCampaignsReturn } from './useHsmCampaigns';
import { useHsmMessages, UseHsmMessagesReturn } from './useHsmMessages';
import { useHsmTemplates, UseHsmTemplatesReturn } from './useHsmTemplates';
import { useHsmStats, UseHsmStatsReturn } from './useHsmStats';

interface UseHsmProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
  canManageHsm: boolean;
}

export interface UseHsmReturn extends UseHsmContactsReturn, UseHsmBatchesReturn, UseHsmCampaignsReturn, UseHsmMessagesReturn, UseHsmTemplatesReturn, UseHsmStatsReturn {
  // Active tab
  activeTab: HsmActiveTab;
  setActiveTab: (tab: HsmActiveTab) => void;
}

export function useHsm({ accessToken, refreshToken, attemptRefresh, canManageHsm }: UseHsmProps): UseHsmReturn {
  // Active tab state
  const [activeTab, setActiveTab] = useState<HsmActiveTab>('contacts');

  // Compose smaller hooks
  const contactsHook = useHsmContacts({
    accessToken,
    refreshToken,
    attemptRefresh,
    canManageHsm
  });

  const batchesHook = useHsmBatches({
    accessToken,
    refreshToken,
    attemptRefresh,
    canManageHsm,
    fetchContacts: contactsHook.fetchContacts
  });

  const campaignsHook = useHsmCampaigns({
    accessToken,
    refreshToken,
    attemptRefresh,
    canManageHsm
  });

  const messagesHook = useHsmMessages({
    accessToken,
    refreshToken,
    attemptRefresh,
    canManageHsm
  });

  const templatesHook = useHsmTemplates({
    accessToken,
    refreshToken,
    attemptRefresh,
    canManageHsm
  });

  const statsHook = useHsmStats({
    accessToken,
    refreshToken,
    attemptRefresh,
    canManageHsm
  });

  return {
    // Active tab
    activeTab,
    setActiveTab,

    // Contacts - spread all from contacts hook
    ...contactsHook,

    // Batches - spread all from batches hook
    ...batchesHook,

    // Campaigns - spread all from campaigns hook
    ...campaignsHook,

    // Messages - spread all from messages hook
    ...messagesHook,

    // Templates - spread all from templates hook
    ...templatesHook,

    // Stats - spread all from stats hook
    ...statsHook
  };
}

// Re-export individual hooks for direct use if needed
export { useHsmContacts } from './useHsmContacts';
export { useHsmBatches } from './useHsmBatches';
export { useHsmCampaigns } from './useHsmCampaigns';
export { useHsmMessages } from './useHsmMessages';
export { useHsmTemplates } from './useHsmTemplates';
export { useHsmStats } from './useHsmStats';

// Re-export types
export type { UseHsmContactsReturn } from './useHsmContacts';
export type { UseHsmBatchesReturn } from './useHsmBatches';
export type { UseHsmCampaignsReturn } from './useHsmCampaigns';
export type { UseHsmMessagesReturn } from './useHsmMessages';
export type { UseHsmTemplatesReturn } from './useHsmTemplates';
export type { UseHsmStatsReturn } from './useHsmStats';
